import { useAppStore } from "@/store/app";
import { useSessionStore } from "@/store/session";
import axios, { AxiosHeaders } from "axios";
import { ApiError } from "@/types/api-error";

export const instance = axios.create({
  baseURL: '/api',
  withCredentials: false,
  headers: {
    Accept: 'application/json',
  },
 
  params: {}
});

// Request interceptor: add Authorization token
instance.interceptors.request.use(
  config => {
    const token = useSessionStore().token;
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor: handle 401
instance.interceptors.response.use(
  response => response,
  error => {
    const status = error?.response?.status;

    // Redirect to login on 401 Unauthorized
    if (status === 401) {
      // Clear any session/token if needed
      useSessionStore().logout?.();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export function handleErrorAndRethrow(error: unknown): never {
  const message = extractErrorMessage(error);
  useAppStore().sendErrorNotice(message);
  throw error;
}

function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Server unreachable. Please try again later.";
    }

    const data = error.response.data;

    if (typeof data === "object" && data && "status" in data && "message" in data) {
      const apiError = data as ApiError;
      return `${apiError.status}: ${apiError.message}`;
    }

    return `${error.response.status}: Unexpected server error`;
  }

  return error instanceof Error ? error.message : "An unexpected error occurred.";
}


// ---------------------------
// Generic request wrapper
// ---------------------------
async function apiRequest<T>(
  request: Promise<T>
): Promise<T | null> {
  try {
    return await request;
  } catch (error) {
    handleErrorAndRethrow(error);
  }
}

// ---------------------------
// API methods
// ---------------------------
export async function getData<T>(url: string): Promise<T | null> {
  const response = await apiRequest(instance.get<T>(url));
  if(!response) return null;
  return response.data;
}

export async function postData<T>(
  url: string,
  data: any
): Promise<T | null> {
  console.log("Post data",url, data )
  const response = await apiRequest(instance.post<T>(url, data));
  if(!response) return null;
  return response.data;
}

export async function putData<T>(
  url: string,
  data?: any
): Promise<T | null> {
  const response = await apiRequest(instance.put<T>(url, data));
  if(!response) return null;
  return response.data;
}

export async function deleteData<T>(
  url: string
): Promise<T | null> {
  const response = await apiRequest(instance.delete<T>(url));
  if(!response) return null;
  return response.data;
}